import { useCallback, useMemo, useState } from "react";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import {
  EntityFileProvider,
  useEntityFileContext,
} from "@/react/components/entityViews/files/context/EntityFileContext";
import { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { FileGridView } from "@/react/components/entityViews/files/components/editors/FileGridView";
import { CompanyCamProjectStatusLink } from "@/react/components/entityViews/files/components/CompanyCamProjectStatusLink";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import type { EntityTypeKey } from "@/other/constants/EntityManagerConfig";
import { FileDetailModal } from "@/react/components/entityViews/files/components/editors/FileDetailModal/FileDetailModal";
import {
  DEFAULT_FILE_BROWSER_FILTERS,
  toFileListFilters,
  type FileBrowserFilterState,
  type FileTypeTab,
} from "@/react/components/entityViews/files/components/editors/FileGridToolbar";
import { SURFACE_ICON_BTN_CLS } from "@/react/components/entityViews/files/constants/surfaceChrome";
import type { EntityRecord } from "@/types/entity";

/** Entity types supported by Manage files / EntityFileProvider. */
type FilesEditorEntityType = typeof ENTITY_TYPES.TASK | typeof ENTITY_TYPES.RESOURCE | typeof ENTITY_TYPES.CLIENT;

export interface TaskFilesEditorRecord {
  /** Preferred: entity record to manage files for. */
  entity?: EntityRecord;
  entityType?: FilesEditorEntityType;
  /**
   * Legacy: when callers only pass `task`, treat as entityType=task.
   * Prefer `entity` + `entityType`.
   */
  task?: EntityRecord;
  initialTab?: FileTypeTab;
}

interface TaskFilesEditorProps {
  record: TaskFilesEditorRecord;
  onClose: () => void;
  editorInstance?: unknown;
  filters: FileBrowserFilterState;
  onFiltersChange: (next: FileBrowserFilterState) => void;
  onSearchChange: (search: string) => void;
}

function resolveFilesEditorTarget(record: TaskFilesEditorRecord): {
  entity: EntityRecord;
  entityType: FilesEditorEntityType;
} {
  if (record.entity && record.entityType) {
    return { entity: record.entity, entityType: record.entityType };
  }
  // Compat: `{ task: entityRecord }` without entityType → treat as task
  const entity = record.entity ?? record.task;
  if (!entity) {
    throw new Error("SwivuTaskFilesEditor: record.entity or record.task is required");
  }
  return {
    entity,
    entityType: record.entityType ?? ENTITY_TYPES.TASK,
  };
}

const TaskFilesEditorContent = ({
  record,
  onClose,
  filters,
  onFiltersChange,
  onSearchChange,
}: TaskFilesEditorProps) => {
  const { entity } = resolveFilesEditorTarget(record);
  const displayName = entity.name ?? "";
  const [navigationFiles, setNavigationFiles] = useState<SwivuFile[]>([]);

  const {
    openedFile,
    openedFileMode,
    clearOpenedFileMode,
    isFileEditorOpen,
    setIsFileEditorOpen,
    entityRecord,
    totalCount,
  } = useEntityFileContext();

  const isFileDetailOpen = Boolean(openedFile && isFileEditorOpen);
  // Compact chrome for Media / Documents manage (Task, Resource, Client);
  // day grouping + multi-select stay Media-only.
  const isMediaSurface = filters.fileTypeTab === "media";
  const isDocumentsSurface = filters.fileTypeTab === "documents";
  const useCompactChrome = isMediaSurface || isDocumentsSurface;

  const dialogTitle = isMediaSurface
    ? "Media"
    : isDocumentsSurface
      ? "Documents"
      : "Manage files";
  const countLabel = isMediaSurface
    ? totalCount === 1
      ? "1 photo"
      : `${totalCount.toLocaleString()} photos`
    : totalCount === 1
      ? "1 document"
      : `${totalCount.toLocaleString()} documents`;
  const closeLabel = isMediaSurface
    ? "Close media"
    : isDocumentsSurface
      ? "Close documents"
      : "Close manage files";

  return (
    <>
      <Dialog
        open
        modal={!isFileDetailOpen}
        onOpenChange={(open) => {
          // Keep Manage files mounted while FileDetailModal is open — that modal
          // lives in .b-float-root and would sit behind this dialog if we closed.
          if (!open && !isFileDetailOpen) onClose();
        }}
      >
        <DialogContent
          className={cn(
            "z-[--swivu-bryntum-z-index] flex flex-col gap-0 overflow-hidden border-border/60 bg-background p-0 shadow-2xl sm:rounded-[2rem]",
            "h-[min(85vh,800px)] max-h-[min(85vh,800px)]",
            "w-[min(1280px,96vw)] max-w-[1280px]",
            // FileDetailModal portals into .b-float-root; hide Manage files while
            // detail is open or the preview sits behind this dialog.
            isFileDetailOpen && "invisible pointer-events-none",
          )}
          showCloseButton={false}
          dialogOverlayClassName={cn(
            "z-[--swivu-bryntum-z-index]",
            isFileDetailOpen && "invisible pointer-events-none",
          )}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => {
            if (isFileDetailOpen) event.preventDefault();
          }}
        >
          <DialogTitle className="sr-only">
            {dialogTitle} — {displayName}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isMediaSurface
              ? `Browse, filter, upload, and manage media for ${displayName}.`
              : isDocumentsSurface
                ? `Browse, filter, upload, and manage documents for ${displayName}.`
                : `Browse, filter, upload, and manage media and documents for ${displayName}.`}
          </DialogDescription>

          <TooltipProvider delayDuration={200}>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <header
                className={cn(
                  "shrink-0 px-5 sm:px-7",
                  useCompactChrome ? "pt-4" : "pt-5",
                )}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h1
                      className={cn(
                        "truncate font-normal leading-tight tracking-tight text-foreground",
                        useCompactChrome
                          ? "text-xl sm:text-2xl"
                          : "text-2xl sm:text-3xl",
                      )}
                    >
                      {dialogTitle}
                    </h1>
                    {useCompactChrome ? (
                      <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm font-normal text-muted-foreground">
                        <span className="min-w-0 truncate">{displayName}</span>
                        <span className="shrink-0 tabular-nums">{countLabel}</span>
                        <CompanyCamProjectStatusLink />
                      </div>
                    ) : (
                      <p className="mt-0.5 truncate text-sm font-normal text-muted-foreground">
                        Media and documents for {displayName}
                      </p>
                    )}
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className={cn(SURFACE_ICON_BTN_CLS, "inline-flex items-center justify-center")}
                        onClick={onClose}
                        aria-label={closeLabel}
                      >
                        <X className="size-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Close</TooltipContent>
                  </Tooltip>
                </div>
              </header>

              <div
                className={cn(
                  "flex min-h-0 flex-1 flex-col overflow-hidden px-5 sm:px-7",
                  useCompactChrome ? "pb-4" : "pb-6",
                )}
              >
                <FileGridView
                  filters={filters}
                  onFiltersChange={onFiltersChange}
                  onSearchChange={onSearchChange}
                  onNavigationFilesChange={setNavigationFiles}
                  groupByDay={isMediaSurface}
                  compactToolbar={useCompactChrome}
                  enableMultiSelect={isMediaSurface}
                />
              </div>
            </div>
          </TooltipProvider>
        </DialogContent>
      </Dialog>

      {openedFile && (
        <FileDetailModal
          entityRecord={entityRecord}
          file={openedFile}
          navigationFiles={navigationFiles}
          open={isFileEditorOpen}
          setOpen={(next) => {
            setIsFileEditorOpen(next);
            if (!next) clearOpenedFileMode();
          }}
          initialViewerMode={openedFileMode}
        />
      )}
    </>
  );
};

const SwivuTaskFilesEditorPopup = (
  args: Omit<TaskFilesEditorProps, "filters" | "onFiltersChange" | "onSearchChange">,
) => {
  const { entity, entityType } = resolveFilesEditorTarget(args.record);

  const [filters, setFilters] = useState<FileBrowserFilterState>(() => ({
    ...DEFAULT_FILE_BROWSER_FILTERS,
    fileTypeTab: args.record.initialTab ?? DEFAULT_FILE_BROWSER_FILTERS.fileTypeTab,
  }));

  const listFilters = useMemo(() => toFileListFilters(filters), [filters]);

  const onSearchChange = useCallback((search: string) => {
    setFilters((prev) => (prev.search === search ? prev : { ...prev, search }));
  }, []);

  return (
    <EntityFileProvider
      record={entity}
      entityType={entityType as EntityTypeKey}
      filters={listFilters}
    >
      <TaskFilesEditorContent
        {...args}
        filters={filters}
        onFiltersChange={setFilters}
        onSearchChange={onSearchChange}
      />
    </EntityFileProvider>
  );
};

export default SwivuTaskFilesEditorPopup;
